import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import SEO from '@/components/site/SEO';
import axios from 'axios';
import {
  TrendingUp, ShieldCheck, BadgeCheck, IndianRupee, BarChart3,
  Leaf, Zap, Umbrella, Phone, ArrowRight, Star, CheckCircle2,
  PieChart, Clock3, Users, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

/* ── fund categories ── */
const FUNDS = [
  {
    id: 'equity',
    icon: TrendingUp,
    color: '#1e73be',
    bg: '#eaf3fb',
    title: 'Equity Funds',
    sub: 'High growth potential',
    desc: 'Invest in a diversified basket of stocks across large-cap, mid-cap and small-cap companies. Ideal for 5+ year horizon.',
    returns: '12–18% p.a.*',
    risk: 'High',
    horizon: '5+ years',
  },
  {
    id: 'debt',
    icon: ShieldCheck,
    color: '#2ea690',
    bg: '#e7f6f2',
    title: 'Debt Funds',
    sub: 'Stable, low-risk returns',
    desc: 'Fixed income instruments like bonds and government securities. Better post-tax returns than FDs for 3+ year holding.',
    returns: '6–9% p.a.*',
    risk: 'Low–Medium',
    horizon: '1–3 years',
  },
  {
    id: 'hybrid',
    icon: PieChart,
    color: '#7c3aed',
    bg: '#f3f0ff',
    title: 'Hybrid Funds',
    sub: 'Best of both worlds',
    desc: 'Balanced allocation between equity and debt — ideal for moderate risk appetite with long-term wealth creation.',
    returns: '9–14% p.a.*',
    risk: 'Medium',
    horizon: '3–5 years',
  },
  {
    id: 'elss',
    icon: Leaf,
    color: '#d97706',
    bg: '#fef9ee',
    title: 'ELSS (Tax Saving)',
    sub: 'Save tax + grow wealth',
    desc: 'Equity Linked Saving Schemes qualify for ₹1.5L deduction under Section 80C with just a 3-year lock-in.',
    returns: '12–16% p.a.*',
    risk: 'High',
    horizon: '3+ years (lock-in)',
  },
];

/* ── client growth stories ── */
const STORIES = [
  {
    name: 'Dr. Anita Sharma',
    role: 'Cardiologist, Mumbai',
    invested: '₹50 L',
    value: '₹78.4 L',
    years: 4,
    gain: '+56.8%',
    color: '#1e73be',
  },
  {
    name: 'Ramesh Agarwal',
    role: 'Business Owner, Pune',
    invested: '₹20 L SIP',
    value: '₹31.6 L',
    years: 5,
    gain: '+58%',
    color: '#2ea690',
  },
  {
    name: 'Priya Mehta',
    role: 'IT Manager, Bengaluru',
    invested: '₹12 L',
    value: '₹18.9 L',
    years: 3,
    gain: '+57.5%',
    color: '#7c3aed',
  },
  {
    name: 'Sunil Nair',
    role: 'CA, Delhi',
    invested: '₹30 L (ELSS)',
    value: '₹44.1 L',
    years: 4,
    gain: '+47%',
    color: '#d97706',
  },
];

/* ── SIP calculator ── */
const SipCalc = () => {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);

  const n = years * 12;
  const r = rate / 100 / 12;
  const futureValue = Math.round(monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r));
  const invested = monthly * n;
  const gains = futureValue - invested;

  return (
    <div className="card-lux p-6">
      <h3 className="font-heading font-bold text-[#0b2545] text-[20px] mb-5">SIP Returns Calculator</h3>
      <div className="space-y-5">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">Monthly SIP</span>
            <span className="font-semibold text-slate-900">₹{monthly.toLocaleString('en-IN')}</span>
          </div>
          <input type="range" min={1000} max={100000} step={1000} value={monthly}
            onChange={e => setMonthly(+e.target.value)}
            className="w-full accent-[#1e73be]" />
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">Duration</span>
            <span className="font-semibold text-slate-900">{years} years</span>
          </div>
          <input type="range" min={1} max={30} step={1} value={years}
            onChange={e => setYears(+e.target.value)}
            className="w-full accent-[#1e73be]" />
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">Expected Returns</span>
            <span className="font-semibold text-slate-900">{rate}% p.a.</span>
          </div>
          <input type="range" min={6} max={20} step={0.5} value={rate}
            onChange={e => setRate(+e.target.value)}
            className="w-full accent-[#1e73be]" />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {[
          { label: 'Invested', val: `₹${(invested / 100000).toFixed(2)}L`, color: '#64748b' },
          { label: 'Est. Returns', val: `₹${(gains / 100000).toFixed(2)}L`, color: '#2ea690' },
          { label: 'Total Value', val: `₹${(futureValue / 100000).toFixed(2)}L`, color: '#1e73be' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-3 text-center border border-[#c9a24b]/20" style={{ backgroundColor: '#FBF9F4' }}>
            <p className="font-heading font-bold text-[17px]" style={{ color: s.color }}>{s.val}</p>
            <p className="text-[11px] text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-3 text-center">*Indicative. Actual returns vary with market conditions.</p>
    </div>
  );
};

/* ── MF Inquiry form ── */
const MFInquiry = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', amount: '', goal: '' });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/forms/submit`, { form_type: 'mf_inquiry', data: form });
    } catch (_) { /* silent */ }
    setLoading(false);
    setDone(true);
  };

  return (
    <div className="card-lux p-8">
      <h3 className="font-heading font-bold text-[#0b2545] text-[22px] mb-1">Start Your MF Journey</h3>
      <p className="text-slate-500 text-sm mb-5">Our AMFI-registered advisor will call you back within 4 hours.</p>
      {done ? (
        <div className="flex items-center gap-3 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <CheckCircle2 className="w-5 h-5 flex-none" />
          <p className="font-medium">Thanks! Our advisor will contact you shortly.</p>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required placeholder="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="h-11 px-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#1e73be]/30 bg-white" />
            <input required placeholder="Mobile Number" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              className="h-11 px-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#1e73be]/30 bg-white" />
          </div>
          <input required type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            className="w-full h-11 px-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#1e73be]/30 bg-white" />
          <input placeholder="Investment Amount (e.g. ₹5,000/month)" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
            className="w-full h-11 px-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#1e73be]/30 bg-white" />
          <select value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })}
            className="w-full h-11 px-3 border border-slate-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-[#1e73be]/30">
            <option value="">Select Investment Goal</option>
            <option>Wealth Creation</option>
            <option>Retirement Planning</option>
            <option>Child Education</option>
            <option>Tax Saving (ELSS)</option>
            <option>Emergency Fund</option>
            <option>Short-term Goals</option>
          </select>
          <button type="submit" disabled={loading}
            className="btn-lux w-full justify-center !py-3 disabled:opacity-60">
            {loading ? 'Submitting...' : <><span>Get Free Consultation</span><ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}
    </div>
  );
};

/* ── Main page ── */
const Investments = () => {
  return (
    <main>
      <SEO
        title="Mutual Fund Investments | AMFI Registered Distributor ARN-336808 | Finirio"
        description="Invest in mutual funds with Finirio — AMFI registered distributor (ARN-336808). Expert guidance on equity, debt, hybrid and ELSS funds. Start SIP from ₹500/month."
        path="/investments"
        keywords="mutual fund investment india, sip investment, elss tax saving fund, finirio mutual fund, arn 336808"
      />
      <PageBanner title="Mutual Fund Investments" crumbs={['Investments']} />

      {/* AMFI Trust Bar */}
      <div className="navy-panel text-white py-3 relative">
        <div className="hairline-gold absolute top-0 left-0 right-0" />
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm">
          <span className="flex items-center gap-2 font-semibold">
            <BadgeCheck className="w-4 h-4 text-[#2ea690]" />
            AMFI Registered Mutual Fund Distributor
          </span>
          <span className="flex items-center gap-2 font-mono font-bold text-[#93c5fd]">
            ARN-336808
          </span>
          <span className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#e8c887]" />
            SEBI Regulated · 100% Transparent
          </span>
        </div>
      </div>

      {/* Hero */}
      <section style={{ background: 'linear-gradient(180deg, #FBF9F4 0%, #f5faff 100%)' }} className="py-16">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#1e73be]/20 text-[13px] font-medium text-[#1e4a86] mb-5">
              <Star className="w-3.5 h-3.5 fill-[#f59e0b] text-[#f59e0b]" /> Trusted by 500+ investors across India
            </span>
            <h1 className="font-heading text-[36px] md:text-[48px] font-extrabold leading-[1.1] tracking-tight text-[#0b2545]">
              Grow your wealth with <span className="text-gold-grad">Mutual Funds</span>
            </h1>
            <p className="mt-4 text-slate-700 text-[16px] leading-relaxed max-w-[560px]">
              As an <strong>AMFI-registered distributor (ARN-336808)</strong>, Finirio offers personalised mutual fund advisory —
              from goal-based planning to fund selection and ongoing portfolio review. Start a SIP from just ₹500/month.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4 max-w-[480px]">
              {[
                { v: '₹500/mo', l: 'Min SIP' },
                { v: '12–18%*', l: 'Equity Returns' },
                { v: '0 Hidden', l: 'Charges' },
              ].map((s) => (
                <div key={s.l} className="rounded-xl bg-white border border-white/60 shadow-sm p-3 text-center">
                  <p className="font-heading font-bold text-[18px]" style={{ color: '#0f3d78' }}>{s.v}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#inquire" className="btn-lux">
                Start Investing <ChevronRight className="w-4 h-4" />
              </a>
              <a href="#funds" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-[15px] font-semibold border border-[#c9a24b]/50 text-[#0b2545] hover:bg-[#c9a24b]/10 transition">
                Explore Funds
              </a>
            </div>
          </div>
          <SipCalc />
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-y border-slate-100 py-8">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Users, v: '500+', l: 'Active MF Investors' },
            { icon: IndianRupee, v: '₹25 Cr+', l: 'AUM Managed' },
            { icon: TrendingUp, v: '15.2%*', l: 'Avg. Portfolio CAGR' },
            { icon: BadgeCheck, v: 'ARN-336808', l: 'AMFI Registered' },
          ].map((s) => (
            <div key={s.l} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: '#eaf3fb' }}>
                <s.icon className="w-5 h-5" style={{ color: '#1e73be' }} />
              </div>
              <div>
                <p className="font-heading font-bold text-[19px] text-slate-900 leading-none">{s.v}</p>
                <p className="text-[12px] text-slate-500 mt-1">{s.l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Fund Categories */}
      <section id="funds" className="py-16" style={{ backgroundColor: '#FBF9F4' }}>
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
          <div className="text-center mb-10">
            <p className="eyebrow-lux mb-2">What We Offer</p>
            <h2 className="font-heading text-[30px] md:text-[38px] font-bold text-slate-900 tracking-tight">Fund Categories</h2>
            <span className="block h-[2px] w-16 mt-4 rounded-full mx-auto" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FUNDS.map((f) => (
              <div key={f.id} className="card-lux p-6 h-full">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl grid place-items-center flex-none" style={{ backgroundColor: f.bg }}>
                    <f.icon className="w-6 h-6" style={{ color: f.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-slate-900 text-[18px]">{f.title}</h3>
                    <p className="text-[13px] font-medium mt-0.5" style={{ color: f.color }}>{f.sub}</p>
                    <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.desc}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-[12px]">
                      <span className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                        Returns: <b>{f.returns}</b>
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                        Risk: <b>{f.risk}</b>
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                        Horizon: <b>{f.horizon}</b>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Growth Stories */}
      <section className="py-16 bg-white">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
          <div className="text-center mb-10">
            <p className="eyebrow-lux mb-2">Real Clients, Real Growth</p>
            <h2 className="font-heading text-[30px] md:text-[38px] font-bold text-slate-900 tracking-tight">
              See How Our Clients Grew Their Wealth
            </h2>
            <span className="block h-[2px] w-16 mt-4 rounded-full mx-auto" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STORIES.map((s) => (
              <div key={s.name} className="card-lux overflow-hidden h-full">
                <div className="py-5 px-5 text-white text-center" style={{ backgroundColor: s.color }}>
                  <div className="w-12 h-12 rounded-full grid place-items-center mx-auto bg-white/20 mb-2 font-heading font-bold text-[20px]">
                    {s.name.charAt(0)}
                  </div>
                  <p className="font-semibold text-[15px]">{s.name}</p>
                  <p className="text-[12px] opacity-80 mt-0.5">{s.role}</p>
                </div>
                <div className="p-5 bg-white">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Invested</span>
                      <span className="font-semibold text-slate-800">{s.invested}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Current Value</span>
                      <span className="font-semibold text-slate-800">{s.value}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Duration</span>
                      <span className="font-semibold text-slate-800">{s.years} yrs</span>
                    </div>
                  </div>
                  <div className="mt-4 rounded-lg py-2 text-center font-heading font-bold text-[22px]"
                    style={{ backgroundColor: '#e7f6f2', color: '#1a7a5e' }}>
                    {s.gain}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[12px] text-slate-400 mt-6">
            *Past performance is not indicative of future returns. Mutual fund investments are subject to market risks.
          </p>
        </div>
      </section>

      {/* Why Finirio MF */}
      <section className="py-16" style={{ backgroundColor: '#FBF9F4' }}>
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
          <div className="text-center mb-10">
            <p className="eyebrow-lux mb-2">Why Choose Us</p>
            <h2 className="font-heading text-[30px] md:text-[38px] font-bold text-slate-900 tracking-tight">
              Why Invest Through Finirio?
            </h2>
            <span className="block h-[2px] w-16 mt-4 rounded-full mx-auto" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BadgeCheck, color: '#1e73be', bg: '#eaf3fb', title: 'AMFI Registered (ARN-336808)', desc: 'Fully regulated and certified distributor. Your investments are processed through SEBI-regulated AMCs.' },
              { icon: Zap, color: '#2ea690', bg: '#e7f6f2', title: 'Goal-Based Planning', desc: 'Retirement, child education, home purchase — we map your goals to the right funds and SIP amounts.' },
              { icon: BarChart3, color: '#7c3aed', bg: '#f3f0ff', title: 'Portfolio Tracking', desc: 'Regular reviews and rebalancing to keep your portfolio aligned with your risk profile and goals.' },
              { icon: Umbrella, color: '#d97706', bg: '#fef9ee', title: 'Zero Hidden Charges', desc: 'Fully transparent fee structure. We earn only via AMC commissions — no advisory fees from you.' },
              { icon: Phone, color: '#1e73be', bg: '#eaf3fb', title: 'Dedicated Advisor', desc: 'A personal relationship manager — reachable by call, WhatsApp, or email — at every step.' },
              { icon: Clock3, color: '#2ea690', bg: '#e7f6f2', title: 'Quick Onboarding', desc: 'Start investing in under 10 minutes. 100% paperless KYC and online form submissions.' },
            ].map((c) => (
              <div key={c.title} className="card-lux p-6 h-full">
                <div className="w-10 h-10 rounded-lg grid place-items-center mb-3" style={{ backgroundColor: c.bg }}>
                  <c.icon className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquire" className="py-16 bg-white">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="eyebrow-lux mb-2">Get Started</p>
            <h2 className="font-heading text-[28px] md:text-[34px] font-bold text-slate-900 tracking-tight">
              Ready to invest smarter?
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Leave your details and our AMFI-registered advisor will design a personalised
              investment plan aligned to your goals and risk profile — absolutely free.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                'Free portfolio health check',
                'Personalised SIP recommendation',
                'ELSS tax-saving strategy',
                'Regular portfolio review calls',
              ].map((pt) => (
                <li key={pt} className="flex items-center gap-3 text-[15px] text-slate-800">
                  <CheckCircle2 className="w-5 h-5 flex-none" style={{ color: '#2ea690' }} />
                  {pt}
                </li>
              ))}
            </ul>
          </div>
          <MFInquiry />
        </div>
      </section>

      {/* Regulatory Disclaimer */}
      <section className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
          <p className="text-[12px] text-slate-500 leading-relaxed text-center max-w-[900px] mx-auto">
            <strong>Regulatory Disclosure:</strong> Finirio Technologies is registered with AMFI as a Mutual Fund Distributor under ARN-336808.
            Mutual fund investments are subject to market risks. Please read all scheme-related documents carefully before investing.
            Past performance is not indicative of future results. Returns mentioned are indicative and based on historical category averages.
            Investments in mutual funds do not guarantee returns. SEBI Investor Helpline: 1800-266-7575.
          </p>
        </div>
      </section>
    </main>
  );
};

export default Investments;
